import React, { FC } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import {
  useModelFilterActions,
  useModelFilters,
} from '@store/zustand/model-filters';
import type { CiaFilterCategory } from '@utils/modelFilterCounts';
import {
  CATEGORY_LABELS,
  CategoryCriteria,
} from '@views/Project/components/ModelFiltersPopover/categories';
import FilterCategoryRow from '@views/Project/components/ModelFiltersPopover/components/FilterCategoryRow';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';

type Props = {
  category: CiaFilterCategory;
  criteria: CategoryCriteria[];
  onOpenCriteria: (criteriaId: string) => void;
  onBack: () => void;
};

/**
 * The criteria list of a CIA category with more than one criteria, shown
 * between the root and the values panel.
 *
 * Assurance level, Coverage and Applicability are configuration-driven: each
 * is a list of criteria and the unit a user selects by is one criteria's
 * answer. A category with a single criteria skips this panel and opens its
 * values directly; with several, this list is the category's AC4.1 panel and
 * each row opens that criteria's values in the same popover. The rows are the
 * root's category rows without an icon, so each criteria carries the AC3.2
 * summary of its own answers and the selection dot. "Clear all" appears
 * while any criteria of the category has a selection and empties the whole
 * category, which is AC4.1.2 at the level this header names.
 */
const FilterCriteriaPanel: FC<Props> = ({
  category,
  criteria,
  onOpenCriteria,
  onBack,
}) => {
  const { ciaFilters } = useModelFilters();
  const { clearCategory } = useModelFilterActions();

  const selection = ciaFilters[category];
  const hasSelection = Object.values(selection).some(
    (answers) => answers.length > 0,
  );

  return (
    <FilterPanelLayout
      title={
        <FilterPanelBackTitle
          title={CATEGORY_LABELS[category]}
          onBack={onBack}
        />
      }
      action={
        hasSelection && (
          <UnderlineTextButton
            $disabled={false}
            onClick={() => clearCategory(category)}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {criteria.map(({ id, name }) => (
        <FilterCategoryRow
          key={id}
          icon={null}
          label={name}
          values={selection[id] ?? []}
          labels={{}}
          onClick={() => onOpenCriteria(id)}
        />
      ))}
    </FilterPanelLayout>
  );
};

export default FilterCriteriaPanel;
